import { useState } from "react";
import { trpc } from "../lib/trpc.js";
import {
  ShieldCheck, Play, RefreshCw, CheckCircle2, AlertTriangle,
  AlertCircle, Info, XCircle, Clock, ChevronDown, ChevronRight,
  FileText, Zap
} from "lucide-react";

const SEVERITY_CONFIG: Record<string, { label: string; color: string; bgColor: string; icon: typeof AlertCircle }> = {
  critical: { label: "Crítico", color: "text-red-400", bgColor: "bg-red-500/10 border-red-500/20", icon: XCircle },
  high: { label: "Alto", color: "text-orange-400", bgColor: "bg-orange-500/10 border-orange-500/20", icon: AlertTriangle },
  medium: { label: "Médio", color: "text-yellow-400", bgColor: "bg-yellow-500/10 border-yellow-500/20", icon: AlertCircle },
  low: { label: "Baixo", color: "text-blue-400", bgColor: "bg-blue-500/10 border-blue-500/20", icon: Info },
  info: { label: "Info", color: "text-muted-foreground", bgColor: "bg-muted/30 border-border", icon: Info },
};

export function Security() {
  const [expandedReport, setExpandedReport] = useState<string | null>(null);
  const [severityFilter, setSeverityFilter] = useState<string>("all");

  const utils = trpc.useUtils();
  const { data: summary } = trpc.security.getSummary.useQuery();
  const { data: reports, isLoading: loadingReports } = trpc.security.listReports.useQuery({ limit: 20 });
  const { data: findings } = trpc.security.getFindings.useQuery(
    { reportId: expandedReport!, severity: severityFilter !== "all" ? severityFilter as any : undefined },
    { enabled: !!expandedReport }
  );

  const runAuditMut = trpc.security.runAudit.useMutation({
    onSuccess: () => utils.security.invalidate(),
  });
  const resolveMut = trpc.security.resolveFinding.useMutation({
    onSuccess: () => utils.security.invalidate(),
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <ShieldCheck className="w-7 h-7 text-primary" />
            Security Auditor
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Auditoria de segurança automatizada do YOS - Megan AI
          </p>
        </div>
        <button
          onClick={() => runAuditMut.mutate()}
          disabled={runAuditMut.isPending}
          className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/80 text-primary-foreground rounded-lg transition-colors disabled:opacity-50"
        >
          {runAuditMut.isPending ? (
            <RefreshCw className="w-4 h-4 animate-spin" />
          ) : (
            <Play className="w-4 h-4" />
          )}
          {runAuditMut.isPending ? "Executando..." : "Executar Auditoria"}
        </button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="text-sm text-muted-foreground">Total</div>
            <div className="text-2xl font-bold text-foreground">{summary.totalFindings}</div>
          </div>
          <div className="bg-card border border-red-500/20 rounded-xl p-4">
            <div className="text-sm text-red-400">Críticos</div>
            <div className="text-2xl font-bold text-red-400">{summary.criticalCount}</div>
          </div>
          <div className="bg-card border border-orange-500/20 rounded-xl p-4">
            <div className="text-sm text-orange-400">Altos</div>
            <div className="text-2xl font-bold text-orange-400">{summary.highCount}</div>
          </div>
          <div className="bg-card border border-yellow-500/20 rounded-xl p-4">
            <div className="text-sm text-yellow-400">Médios</div>
            <div className="text-2xl font-bold text-yellow-400">{summary.mediumCount}</div>
          </div>
          <div className="bg-card border border-blue-500/20 rounded-xl p-4">
            <div className="text-sm text-blue-400">Não Resolvidos</div>
            <div className="text-2xl font-bold text-blue-400">{summary.unresolvedCount}</div>
          </div>
        </div>
      )}

      {/* No summary = no audits yet */}
      {!summary && !loadingReports && (
        <div className="bg-card border border-border rounded-xl p-8 text-center">
          <ShieldCheck className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-foreground mb-2">Nenhuma auditoria realizada</h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            Execute sua primeira auditoria de segurança para verificar variáveis de ambiente, vault, dependências e configurações do sistema.
          </p>
        </div>
      )}

      {/* Latest Audit Result (if just ran) */}
      {runAuditMut.data && (
        <div className="bg-card border border-success/20 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-foreground mb-2 flex items-center gap-2">
            <Zap className="w-5 h-5 text-success" />
            Resultado da Auditoria
          </h3>
          <div className="text-sm text-muted-foreground mb-4">
            {runAuditMut.data.totalFindings} achados em {runAuditMut.data.duration}ms
          </div>
          {runAuditMut.data.totalFindings === 0 ? (
            <div className="flex items-center gap-2 text-success">
              <CheckCircle2 className="w-5 h-5" />
              <span className="text-sm font-medium">Nenhum problema encontrado. Sistema seguro.</span>
            </div>
          ) : (
            <div className="space-y-2">
              {runAuditMut.data.findings.map((f: any, i: number) => {
                const sev = SEVERITY_CONFIG[f.severity] || SEVERITY_CONFIG.info;
                const SevIcon = sev.icon;
                return (
                  <div key={i} className={`border rounded-lg p-3 ${sev.bgColor}`}>
                    <div className="flex items-start gap-2">
                      <SevIcon className={`w-4 h-4 mt-0.5 ${sev.color}`} />
                      <div>
                        <div className="text-foreground text-sm font-medium">{f.message}</div>
                        {f.recommendation && (
                          <div className="text-xs text-muted-foreground mt-1">{f.recommendation}</div>
                        )}
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`text-xs ${sev.color}`}>{sev.label}</span>
                          <span className="text-xs text-muted-foreground/50">{f.type}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Reports History */}
      {reports && reports.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-muted-foreground" />
            Histórico de Auditorias
          </h3>
          <div className="space-y-2">
            {reports.map((report: any) => (
              <div key={report.id}>
                <button
                  onClick={() => setExpandedReport(expandedReport === report.id ? null : report.id)}
                  className="w-full bg-card border border-border rounded-xl p-4 flex items-center justify-between hover:border-primary/30 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    {expandedReport === report.id ? (
                      <ChevronDown className="w-4 h-4 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    )}
                    <div className="text-left">
                      <div className="text-foreground font-medium">
                        Auditoria #{report.id.slice(0, 8)}
                      </div>
                      <div className="text-sm text-muted-foreground flex items-center gap-2">
                        <Clock className="w-3 h-3" />
                        {new Date(report.createdAt).toLocaleString("pt-BR")}
                        <span className="text-muted-foreground/30">|</span>
                        {report.duration}ms
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {report.criticalCount > 0 && (
                      <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded-full">
                        {report.criticalCount} críticos
                      </span>
                    )}
                    {report.highCount > 0 && (
                      <span className="px-2 py-1 bg-orange-500/10 text-orange-400 text-xs rounded-full">
                        {report.highCount} altos
                      </span>
                    )}
                    <span className="text-sm text-muted-foreground">
                      {report.totalFindings} achados
                    </span>
                  </div>
                </button>

                {/* Expanded Findings */}
                {expandedReport === report.id && (
                  <div className="mt-2 ml-8 space-y-2">
                    <div className="flex gap-1 mb-3">
                      {["all", "critical", "high", "medium", "low", "info"].map((sev) => (
                        <button
                          key={sev}
                          onClick={() => setSeverityFilter(sev)}
                          className={`px-3 py-1 rounded-lg text-xs transition-colors ${
                            severityFilter === sev
                              ? "bg-primary/20 text-foreground border border-primary/30"
                              : "bg-muted/30 text-muted-foreground hover:text-foreground border border-transparent"
                          }`}
                        >
                          {sev === "all" ? "Todos" : SEVERITY_CONFIG[sev]?.label || sev}
                        </button>
                      ))}
                    </div>
                    {findings?.length === 0 && (
                      <div className="text-center py-4 text-muted-foreground text-sm">
                        Nenhum achado com este filtro
                      </div>
                    )}
                    {findings?.map((f: any) => {
                      const sev = SEVERITY_CONFIG[f.severity] || SEVERITY_CONFIG.info;
                      const SevIcon = sev.icon;
                      return (
                        <div key={f.id} className={`border rounded-lg p-3 ${sev.bgColor}`}>
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-2">
                              <SevIcon className={`w-4 h-4 mt-0.5 ${sev.color}`} />
                              <div>
                                <div className="text-foreground text-sm">{f.message}</div>
                                {f.recommendation && (
                                  <div className="text-xs text-muted-foreground mt-1">{f.recommendation}</div>
                                )}
                              </div>
                            </div>
                            {!f.resolved ? (
                              <button
                                onClick={() => resolveMut.mutate({ id: f.id })}
                                disabled={resolveMut.isPending}
                                className="px-2 py-1 bg-primary/20 text-primary text-xs rounded hover:bg-primary/30 transition-colors disabled:opacity-50"
                              >
                                Resolver
                              </button>
                            ) : (
                              <span className="flex items-center gap-1 text-xs text-success">
                                <CheckCircle2 className="w-3 h-3" /> Resolvido
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
